import PageHero from "../components/site/PageHero";
import CTABanner from "../components/site/CTABanner";
import FAQBlock from "../components/site/FAQBlock";
import useSEO from "../hooks/useSEO";
import {
  Flame,
  Camera,
  DoorOpen,
  Radio,
  Wrench,
  ClipboardCheck,
  CalendarClock,
  ListChecks,
} from "lucide-react";

const IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_fire-cctv-access/artifacts/lmp91ro0_ChatGPT%20Image%20Aug%207%2C%202026%2C%2001_52_44%20PM.png";

const SECTIONS = [
  {
    icon: Flame,
    title: "Fire System Maintenance",
    items: [
      "Fire alarm servicing",
      "Emergency lighting inspections",
      "Fire extinguisher servicing",
      "Fault diagnosis",
      "Component replacement",
      "Planned preventative maintenance",
    ],
    to: "/services",
  },
  {
    icon: Camera,
    title: "CCTV & Security Maintenance",
    items: [
      "Camera checks",
      "Image quality checks",
      "Recorder and storage checks",
      "Remote access checks",
      "Intruder alarm testing",
      "Detector testing",
      "Battery replacement",
      "Fault diagnosis and upgrades",
    ],
    to: "/cctv-security",
  },
  {
    icon: DoorOpen,
    title: "Access Control Maintenance",
    items: [
      "Reader testing",
      "Door release testing",
      "Lock checks",
      "Intercom testing",
      "User management support",
      "Gate and barrier testing (where offered)",
      "Fault diagnosis",
      "Component replacement",
    ],
    to: "/access-control",
  },
  {
    icon: Radio,
    title: "Monitoring Support",
    items: [
      "Communication checks",
      "Alert testing",
      "Keyholder updates",
      "Response plan reviews",
      "Fault notification testing",
      "Device testing",
      "Monitoring connection checks",
    ],
    to: "/monitoring",
  },
];

export default function MaintenanceSupport() {
  useSEO({
    title: "System Maintenance, Repairs & Support Ireland | FSS",
    description:
      "Cross-division maintenance across fire safety, CCTV, security, access control and monitoring systems — planned preventative maintenance, fault diagnosis and multi-site support.",
    canonicalPath: "/maintenance-support",
  });

  return (
    <>
      <PageHero
        eyebrow="Maintenance & Support"
        title="System maintenance, repairs and support."
        subtitle="FSS provides maintenance and technical support across fire safety, CCTV, security, access control and monitoring systems. Regular maintenance helps identify faults, maintain system reliability, extend equipment life and provide greater visibility over future repair or upgrade requirements."
        image={IMG}
        imageAlt="Engineer servicing an integrated security system"
        ctaPrimary={{ to: "/contact", label: "Request a Maintenance Proposal" }}
        ctaSecondary={{ to: "/contact", label: "Contact Our Team" }}
      />

      <section className="fss-section bg-white">
        <div className="fss-container">
          <div className="max-w-2xl mb-10">
            <div className="fss-overline">Cross-division coverage</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              One partner for every system in your building
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {SECTIONS.map((s, i) => (
              <div key={i} className="fss-card fss-card-hover" data-testid={`maintenance-section-${i}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 border border-slate-200 flex items-center justify-center bg-slate-50 rounded-sm">
                    <s.icon className="w-5 h-5 text-green-700" strokeWidth={1.75} />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-slate-900">{s.title}</h3>
                </div>
                <ul className="mt-5 space-y-2 text-sm text-slate-700">
                  {s.items.map((it, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-700 mt-2 shrink-0" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 fss-section border-y border-slate-200">
        <div className="fss-container">
          <div className="max-w-2xl mb-10">
            <div className="fss-overline">Planned preventative maintenance</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Structured programmes for single or multi-site organisations
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: CalendarClock, title: "Agreed service schedules", copy: "Programmes matched to the equipment installed and your operating requirements." },
              { icon: ClipboardCheck, title: "Routine system checks", copy: "Scheduled checks across each division to help maintain reliability." },
              { icon: ListChecks, title: "Service records", copy: "Clear documentation covering the work carried out at each visit." },
              { icon: Wrench, title: "Fault reporting", copy: "Reported faults with recommended remedial actions and repair quotations." },
            ].map((it, i) => (
              <div key={i} className="fss-card fss-card-hover">
                <it.icon className="w-8 h-8 text-green-700" strokeWidth={1.75} />
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{it.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{it.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FAQBlock
        eyebrow="Maintenance FAQ"
        title="Frequently asked questions"
        items={[
          { q: "Can FSS maintain systems installed by another provider?", a: "In many cases yes — we assess the equipment, confirm what is required and advise on the appropriate maintenance approach." },
          { q: "Can FSS cover multiple systems under one plan?", a: "Yes — a single maintenance programme can cover fire safety, CCTV, access control and monitoring systems across your portfolio." },
          { q: "What documentation is provided?", a: "Clear service information covering the equipment tested, work carried out and any recommended remedial actions." },
          { q: "What happens between planned visits?", a: "Contact us and we will advise on the appropriate next step — including reactive support where needed." },
        ]}
      />

      <CTABanner
        title="Discuss a planned maintenance programme."
        subtitle="Talk to our team about a maintenance programme tailored to the systems in your premises."
        primary={{ to: "/contact", label: "Request a Maintenance Proposal" }}
        secondary={{ to: "/contact", label: "Contact Our Team" }}
      />
    </>
  );
}
